import type { VisualStyle } from './visualStyles.js';

/**
 * Звуковая тема каждого визуального стиля: своя музыка (лад, прогрессия, темп,
 * тембры, фильтр) и свои звуки хода/пары/победы — всё синтезируется в Web Audio,
 * без файлов (как gearSound.ts / backgroundMusic.ts).
 *
 * Черновик таблицы THEMES составлен через бесплатную LLM-квоту (FreeLLMAPI) по
 * образу каждого стиля, затем проверен на допустимые диапазоны и уникальность
 * и выровнена по громкости (musicGain/sfxGain — замер OfflineAudioContext; см. тесты audio-themes.test.ts) и поправлен вручную (chain.sfx.noise,
 * demon.arpPattern/scale). Правьте значения здесь — тесты подскажут границы.
 */

export type ScaleName =
  | 'major'
  | 'minor'
  | 'dorian'
  | 'phrygian'
  | 'lydian'
  | 'mixolydian'
  | 'pentatonicMajor'
  | 'pentatonicMinor'
  | 'harmonicMinor'
  | 'wholeTone';

export type ThemeWave = 'sine' | 'triangle' | 'square' | 'sawtooth';

export interface SfxTheme {
  wave: ThemeWave;
  /** Длина звука хода, сек. */
  decay: number;
  /** Доля шумового всплеска в звуке хода (0 = чистый тон). */
  noise: number;
  /** Глиссандо тона хода: + вверх, − вниз (в октавах). */
  glide: number;
}

export interface AudioTheme {
  /** Тоника (MIDI) в басовом регистре. */
  rootMidi: number;
  scale: ScaleName;
  /** Ступени лада — корни 4 аккордов, по 8 восьмых каждый. */
  progression: number[];
  /** Длительность восьмой, сек. */
  stepSec: number;
  /** 8 индексов нот аккорда (0..3) для арпеджио. */
  arpPattern: number[];
  padWave: ThemeWave;
  arpWave: ThemeWave;
  bassWave: ThemeWave;
  /** Lowpass всей музыки, Гц. */
  filterHz: number;
  /** На сколько октав выше аккорда играет арпеджио. */
  arpOctave: 0 | 1 | 2;
  sfx: SfxTheme;
  /** Выравнивание громкости музыки между стилями (замер офлайн-рендером, цель RMS≈0.036). */
  musicGain: number;
  /** То же для звуков хода/пары/победы (цель RMS≈0.022). */
  sfxGain: number;
}

/** Поправка громкости на тембр: square/saw богаче гармониками и звучат громче sine при том же уровне. */
export function waveGain(wave: ThemeWave): number {
  switch (wave) {
    case 'sine':
      return 1;
    case 'triangle':
      return 0.9;
    case 'square':
      return 0.45;
    case 'sawtooth':
      return 0.5;
  }
}

export const SCALE_INTERVALS: Record<ScaleName, number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  pentatonicMajor: [0, 2, 4, 7, 9],
  pentatonicMinor: [0, 3, 5, 7, 10],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
  wholeTone: [0, 2, 4, 6, 8, 10],
};

/** MIDI-нота ступени лада `degree` (может быть >длины лада или отрицательной — уйдёт в другую октаву). */
export function scaleDegreeToMidi(theme: AudioTheme, degree: number): number {
  const iv = SCALE_INTERVALS[theme.scale];
  const len = iv.length;
  const octave = Math.floor(degree / len);
  const idx = ((degree % len) + len) % len;
  return theme.rootMidi + octave * 12 + iv[idx]!;
}

export interface ThemeChord {
  bass: number;
  notes: number[];
}

/** Аккорды прогрессии темы: бас + четыре ноты (терция-квинта-октава от корня, ×регистр +12). */
export function buildChords(theme: AudioTheme): ThemeChord[] {
  const len = SCALE_INTERVALS[theme.scale].length;
  return theme.progression.map((deg) => {
    let bass = scaleDegreeToMidi(theme, deg);
    if (bass >= theme.rootMidi + 7) bass -= 12; // бас держим низко
    const notes = [0, 2, 4, len].map((k) => scaleDegreeToMidi(theme, deg + k) + 12);
    return { bass, notes };
  });
}

export const AUDIO_THEMES: Record<VisualStyle, AudioTheme> = {
  pipe: {
    musicGain: 0.95,
    sfxGain: 0.77,
    rootMidi: 40,
    scale: 'major',
    progression: [0, 4, 5, 3],
    stepSec: 0.32,
    arpPattern: [0, 1, 2, 3, 2, 1, 0, 1],
    padWave: 'sine',
    arpWave: 'triangle',
    bassWave: 'sine',
    filterHz: 1400,
    arpOctave: 1,
    sfx: { wave: 'triangle', decay: 0.3, noise: 0.7, glide: 0.1 },
  },
  snake: {
    musicGain: 0.93,
    sfxGain: 0.6,
    rootMidi: 42,
    scale: 'phrygian',
    progression: [0, 1, 4, 0],
    stepSec: 0.42,
    arpPattern: [0, 3, 2, 1, 0, 2, 1, 3],
    padWave: 'sine',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 1800,
    arpOctave: 1,
    sfx: { wave: 'sine', decay: 0.32, noise: 0.1, glide: -0.3 },
  },
  gear: {
    musicGain: 1.26,
    sfxGain: 1.35,
    rootMidi: 45,
    scale: 'minor',
    progression: [0, 0, 4, 4],
    stepSec: 0.28,
    arpPattern: [0, 0, 1, 1, 2, 2, 3, 3],
    padWave: 'triangle',
    arpWave: 'square',
    bassWave: 'sine',
    filterHz: 2800,
    arpOctave: 1,
    sfx: { wave: 'square', decay: 0.09, noise: 0.8, glide: 0.0 },
  },
  chain: {
    musicGain: 2.15,
    sfxGain: 1.52,
    rootMidi: 38,
    scale: 'minor',
    progression: [0, 2, 1, 3],
    stepSec: 0.5,
    arpPattern: [0, 1, 0, 2, 0, 3, 0, 1],
    padWave: 'sawtooth',
    arpWave: 'sawtooth',
    bassWave: 'triangle',
    filterHz: 1300,
    arpOctave: 0,
    sfx: { wave: 'sawtooth', decay: 0.35, noise: 0.8, glide: -0.2 },
  },
  rope: {
    musicGain: 1.22,
    sfxGain: 0.78,
    rootMidi: 47,
    scale: 'dorian',
    progression: [0, 5, 1, 4],
    stepSec: 0.45,
    arpPattern: [1, 2, 3, 1, 0, 2, 3, 0],
    padWave: 'triangle',
    arpWave: 'triangle',
    bassWave: 'triangle',
    filterHz: 2000,
    arpOctave: 1,
    sfx: { wave: 'triangle', decay: 0.22, noise: 0.25, glide: 0.15 },
  },
  wave: {
    musicGain: 0.9,
    sfxGain: 0.6,
    rootMidi: 48,
    scale: 'wholeTone',
    progression: [0, 1, 0, 2],
    stepSec: 0.55,
    arpPattern: [0, 1, 0, 2, 1, 0, 2, 1],
    padWave: 'sine',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 2600,
    arpOctave: 1,
    sfx: { wave: 'sine', decay: 0.3, noise: 0.15, glide: 0.05 },
  },
  crystal: {
    musicGain: 0.95,
    sfxGain: 0.6,
    rootMidi: 49,
    scale: 'major',
    progression: [0, 3, 4, 5],
    stepSec: 0.35,
    arpPattern: [3, 2, 1, 0, 1, 2, 3, 2],
    padWave: 'sine',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 4200,
    arpOctave: 2,
    sfx: { wave: 'sine', decay: 0.18, noise: 0.0, glide: 0.0 },
  },
  lightning: {
    musicGain: 1.08,
    sfxGain: 2.05,
    rootMidi: 44,
    scale: 'minor',
    progression: [0, 5, 0, 4],
    stepSec: 0.19,
    arpPattern: [0, 2, 1, 3, 2, 0, 3, 1],
    padWave: 'sine',
    arpWave: 'sawtooth',
    bassWave: 'sine',
    filterHz: 3000,
    arpOctave: 2,
    sfx: { wave: 'sawtooth', decay: 0.08, noise: 0.4, glide: 0.35 },
  },
  spring: {
    musicGain: 1.32,
    sfxGain: 0.88,
    rootMidi: 50,
    scale: 'major',
    progression: [0, 5, 3, 4],
    stepSec: 0.23,
    arpPattern: [0, 2, 0, 2, 1, 3, 1, 3],
    padWave: 'triangle',
    arpWave: 'sine',
    bassWave: 'triangle',
    filterHz: 3400,
    arpOctave: 2,
    sfx: { wave: 'triangle', decay: 0.12, noise: 0.05, glide: 0.45 },
  },
  thread: {
    musicGain: 0.91,
    sfxGain: 0.6,
    rootMidi: 48,
    scale: 'major',
    progression: [0, 2, 4, 5],
    stepSec: 0.52,
    arpPattern: [0, 1, 2, 1, 3, 2, 1, 0],
    padWave: 'sine',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 3600,
    arpOctave: 2,
    sfx: { wave: 'sine', decay: 0.28, noise: 0.05, glide: 0.05 },
  },
  bubble: {
    musicGain: 0.98,
    sfxGain: 0.64,
    rootMidi: 46,
    scale: 'major',
    progression: [0, 4, 5, 0],
    stepSec: 0.26,
    arpPattern: [0, 2, 1, 3, 2, 0, 3, 1],
    padWave: 'sine',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 2400,
    arpOctave: 2,
    sfx: { wave: 'sine', decay: 0.15, noise: 0.25, glide: 0.35 },
  },
  ribbon: {
    musicGain: 0.94,
    sfxGain: 0.69,
    rootMidi: 49,
    scale: 'lydian',
    progression: [0, 4, 5, 1],
    stepSec: 0.38,
    arpPattern: [0, 1, 3, 2, 0, 2, 3, 1],
    padWave: 'sine',
    arpWave: 'triangle',
    bassWave: 'sine',
    filterHz: 3000,
    arpOctave: 2,
    sfx: { wave: 'triangle', decay: 0.3, noise: 0.1, glide: 0.2 },
  },
  fire: {
    musicGain: 2.78,
    sfxGain: 1.75,
    rootMidi: 40,
    scale: 'harmonicMinor',
    progression: [0, 3, 0, 2],
    stepSec: 0.34,
    arpPattern: [0, 1, 0, 3, 0, 1, 0, 3],
    padWave: 'sawtooth',
    arpWave: 'sawtooth',
    bassWave: 'sawtooth',
    filterHz: 1900,
    arpOctave: 1,
    sfx: { wave: 'sawtooth', decay: 0.18, noise: 0.55, glide: 0.1 },
  },
  vine: {
    musicGain: 1.15,
    sfxGain: 0.75,
    rootMidi: 47,
    scale: 'pentatonicMinor',
    progression: [0, 3, 4, 2],
    stepSec: 0.46,
    arpPattern: [0, 1, 2, 1, 3, 2, 1, 0],
    padWave: 'triangle',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 2200,
    arpOctave: 1,
    sfx: { wave: 'triangle', decay: 0.25, noise: 0.3, glide: 0.05 },
  },
  magnet: {
    musicGain: 0.89,
    sfxGain: 0.6,
    rootMidi: 38,
    scale: 'mixolydian',
    progression: [0, 3, 1, 2],
    stepSec: 0.54,
    arpPattern: [0, 0, 1, 0, 0, 2, 0, 0],
    padWave: 'sine',
    arpWave: 'triangle',
    bassWave: 'sine',
    filterHz: 1000,
    arpOctave: 1,
    sfx: { wave: 'sine', decay: 0.35, noise: 0.1, glide: -0.5 },
  },
  candy: {
    musicGain: 1.01,
    sfxGain: 0.67,
    rootMidi: 50,
    scale: 'major',
    progression: [0, 2, 5, 4],
    stepSec: 0.22,
    arpPattern: [0, 2, 1, 3, 0, 2, 1, 3],
    padWave: 'sine',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 4200,
    arpOctave: 2,
    sfx: { wave: 'sine', decay: 0.1, noise: 0.0, glide: 0.15 },
  },
  bone: {
    musicGain: 1.23,
    sfxGain: 1.05,
    rootMidi: 42,
    scale: 'minor',
    progression: [0, 1, 3, 0],
    stepSec: 0.27,
    arpPattern: [0, 1, 0, 0, 1, 2, 0, 1],
    padWave: 'triangle',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 2200,
    arpOctave: 1,
    sfx: { wave: 'triangle', decay: 0.08, noise: 0.8, glide: 0.0 },
  },
  magic: {
    musicGain: 0.93,
    sfxGain: 0.6,
    rootMidi: 49,
    scale: 'lydian',
    progression: [0, 4, 1, 5],
    stepSec: 0.4,
    arpPattern: [0, 3, 1, 2, 0, 3, 1, 2],
    padWave: 'sine',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 3800,
    arpOctave: 2,
    sfx: { wave: 'sine', decay: 0.25, noise: 0.05, glide: 0.2 },
  },
  cyberpunk: {
    musicGain: 2.99,
    sfxGain: 1.24,
    rootMidi: 44,
    scale: 'mixolydian',
    progression: [0, 4, 0, 5],
    stepSec: 0.2,
    arpPattern: [0, 1, 0, 1, 2, 3, 2, 3],
    padWave: 'sawtooth',
    arpWave: 'sawtooth',
    bassWave: 'sawtooth',
    filterHz: 3600,
    arpOctave: 1,
    sfx: { wave: 'square', decay: 0.1, noise: 0.3, glide: 0.25 },
  },
  goth: {
    musicGain: 2.1,
    sfxGain: 1.36,
    rootMidi: 40,
    scale: 'harmonicMinor',
    progression: [0, 2, 5, 3],
    stepSec: 0.48,
    arpPattern: [0, 1, 2, 0, 3, 2, 1, 0],
    padWave: 'sawtooth',
    arpWave: 'triangle',
    bassWave: 'triangle',
    filterHz: 1600,
    arpOctave: 1,
    sfx: { wave: 'sawtooth', decay: 0.35, noise: 0.2, glide: -0.1 },
  },
  robot: {
    musicGain: 1.83,
    sfxGain: 1.31,
    rootMidi: 45,
    scale: 'pentatonicMajor',
    progression: [0, 2, 3, 0],
    stepSec: 0.24,
    arpPattern: [0, 0, 0, 0, 1, 1, 1, 1],
    padWave: 'square',
    arpWave: 'square',
    bassWave: 'square',
    filterHz: 2600,
    arpOctave: 1,
    sfx: { wave: 'square', decay: 0.09, noise: 0.6, glide: 0.0 },
  },
  wheel: {
    musicGain: 1.29,
    sfxGain: 0.87,
    rootMidi: 43,
    scale: 'major',
    progression: [0, 3, 0, 4],
    stepSec: 0.3,
    arpPattern: [0, 1, 1, 0, 2, 3, 3, 2],
    padWave: 'triangle',
    arpWave: 'triangle',
    bassWave: 'triangle',
    filterHz: 1800,
    arpOctave: 1,
    sfx: { wave: 'triangle', decay: 0.16, noise: 0.4, glide: 0.0 },
  },
  emo: {
    musicGain: 0.91,
    sfxGain: 0.6,
    rootMidi: 44,
    scale: 'minor',
    progression: [0, 4, 1, 5],
    stepSec: 0.5,
    arpPattern: [0, 1, 2, 1, 2, 3, 1, 0],
    padWave: 'sine',
    arpWave: 'triangle',
    bassWave: 'sine',
    filterHz: 2100,
    arpOctave: 2,
    sfx: { wave: 'sine', decay: 0.3, noise: 0.05, glide: -0.2 },
  },
  armor: {
    musicGain: 2.67,
    sfxGain: 1.54,
    rootMidi: 39,
    scale: 'mixolydian',
    progression: [0, 3, 4, 0],
    stepSec: 0.44,
    arpPattern: [0, 0, 1, 1, 2, 2, 3, 3],
    padWave: 'sawtooth',
    arpWave: 'sawtooth',
    bassWave: 'sawtooth',
    filterHz: 1400,
    arpOctave: 1,
    sfx: { wave: 'sawtooth', decay: 0.28, noise: 0.5, glide: -0.15 },
  },
  chrome: {
    musicGain: 0.98,
    sfxGain: 0.63,
    rootMidi: 46,
    scale: 'wholeTone',
    progression: [0, 4, 1, 3],
    stepSec: 0.33,
    arpPattern: [1, 2, 3, 1, 0, 2, 3, 1],
    padWave: 'sine',
    arpWave: 'sawtooth',
    bassWave: 'sine',
    filterHz: 3400,
    arpOctave: 2,
    sfx: { wave: 'sine', decay: 0.15, noise: 0.15, glide: 0.15 },
  },
  viking: {
    musicGain: 2.2,
    sfxGain: 1.74,
    rootMidi: 41,
    scale: 'phrygian',
    progression: [0, 2, 4, 0],
    stepSec: 0.4,
    arpPattern: [0, 1, 2, 3, 1, 0, 3, 2],
    padWave: 'sawtooth',
    arpWave: 'sawtooth',
    bassWave: 'triangle',
    filterHz: 2000,
    arpOctave: 1,
    sfx: { wave: 'sawtooth', decay: 0.18, noise: 0.45, glide: -0.1 },
  },
  samurai: {
    musicGain: 1.19,
    sfxGain: 0.69,
    rootMidi: 43,
    scale: 'pentatonicMinor',
    progression: [0, 3, 0, 1],
    stepSec: 0.37,
    arpPattern: [0, 1, 0, 2, 1, 0, 3, 0],
    padWave: 'triangle',
    arpWave: 'sine',
    bassWave: 'sine',
    filterHz: 2500,
    arpOctave: 1,
    sfx: { wave: 'sine', decay: 0.12, noise: 0.5, glide: -0.45 },
  },
  zombie: {
    musicGain: 1.22,
    sfxGain: 0.6,
    rootMidi: 39,
    scale: 'harmonicMinor',
    progression: [0, 1, 0, 4],
    stepSec: 0.52,
    arpPattern: [0, 0, 1, 0, 0, 2, 0, 3],
    padWave: 'triangle',
    arpWave: 'sine',
    bassWave: 'triangle',
    filterHz: 1200,
    arpOctave: 1,
    sfx: { wave: 'sine', decay: 0.3, noise: 0.5, glide: -0.4 },
  },
  toxic: {
    musicGain: 2.83,
    sfxGain: 1.82,
    rootMidi: 46,
    scale: 'wholeTone',
    progression: [0, 4, 3, 1],
    stepSec: 0.29,
    arpPattern: [1, 3, 0, 2, 3, 1, 0, 2],
    padWave: 'sawtooth',
    arpWave: 'sawtooth',
    bassWave: 'sawtooth',
    filterHz: 3200,
    arpOctave: 2,
    sfx: { wave: 'sawtooth', decay: 0.14, noise: 0.35, glide: 0.3 },
  },
  demon: {
    musicGain: 2.69,
    sfxGain: 1.58,
    rootMidi: 38,
    scale: 'phrygian',
    progression: [0, 5, 2, 4],
    stepSec: 0.44,
    arpPattern: [0, 3, 1, 2, 0, 2, 0, 3],
    padWave: 'sawtooth',
    arpWave: 'sawtooth',
    bassWave: 'sawtooth',
    filterHz: 1500,
    arpOctave: 1,
    sfx: { wave: 'sawtooth', decay: 0.25, noise: 0.4, glide: -0.3 },
  },
};

export function getAudioTheme(style: VisualStyle): AudioTheme {
  return AUDIO_THEMES[style];
}
